import React from "react";
import { PAYMENT_OPTIONS, LABELS } from "./OrderSummaryConstants";
import { PaymentMethodsProps } from "./types";

interface ExtendedPaymentMethodsProps extends PaymentMethodsProps {
  isFullyPaid?: boolean;
  orderStatus?: string;
  openCashModal: () => void;
  openCreditModal: () => void;
}

const PaymentMethods: React.FC<ExtendedPaymentMethodsProps> = ({ 
  selected, 
  onChange, 
  remainingAmount, 
  status, 
  isFullyPaid, 
  orderStatus,
  openCashModal,
  openCreditModal 
}) => {
  // Don't show payment methods if order is cancelled
  if (orderStatus === "Cancelled") {
    return null;
  }

  // Don't show payment methods if order is fully paid
  if (isFullyPaid) {
    return (
      <div className="mb-4">
        <p className="text-lg font-semibold mb-2">{LABELS.PAYMENT_METHODS}</p>
        <div className="text-center py-4 bg-green-50 rounded border border-green-200">
          <p className="text-green-700 font-medium">No remaining balance - Order is ready for delivery</p>
        </div>
      </div>
    );
  }

  // Original logic for pending status with remaining amount
  if (status !== "Pending" || !remainingAmount || remainingAmount <= 0) return null;

  // Handle payment method selection
  const handlePaymentSelection = (method: string) => {
    // Trigger the onChange for radio button
    const syntheticEvent = {
      target: {
        value: method,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
    
    // Open the corresponding modal
    if (method === "Cash") {
      openCashModal();
    } else if (method === "Card") {
      openCreditModal(); // Or open a separate card modal if you have one
    }
  };

  return (
    <div className="mb-4">
      <p className="text-lg font-semibold mb-2">{LABELS.PAYMENT_METHODS}</p>
      <ul className="flex justify-center gap-10 items-center">
        {PAYMENT_OPTIONS.map((method) => (
          <li key={method}>
            <button
              type="button"
              onClick={() => handlePaymentSelection(method)}
              className={`flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg transition-all ${
                selected === method 
                  ? 'bg-blue-100 text-blue-700 border-2 border-blue-500' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
              }`}
            >
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selected === method ? 'border-blue-500' : 'border-gray-400'
              }`}>
                {selected === method && (
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                )}
              </div>
              <span className="font-medium">{method}</span>
            </button>
          </li>
        ))}
      </ul>
      
      {/* Show selected payment method info */}
      {selected && remainingAmount > 0 && (
        <div className="mt-3 text-center">
          <p className="text-sm text-gray-600">
            Selected: <span className="font-semibold">{selected}</span> - 
            Remaining: <span className="font-semibold">{remainingAmount} QAR</span>
          </p>
          <button
            type="button"
            onClick={() => {
              if (selected === "Cash") {
                openCashModal();
              } else if (selected === "Card") {
                openCreditModal();
              }
            }}
            className="mt-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
          >
            Process {selected} Payment
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentMethods;